import { useState, useRef, useImperativeHandle, forwardRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react'
import { createPortal, flushSync } from 'react-dom'
import { X, MessageCircle, Paperclip, Send, Mic, EyeOff, Copy, Check, ThumbsUp, ThumbsDown, Volume2, VolumeX } from 'lucide-react'
import { Button }   from '@/components/ui/button'
import { Input }    from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label }    from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel } from '@/components/ui/select'
import { toIso, fromIso, safeInvoke, OVERLAY_Z_INDEX, generateId } from '@/lib/utils'
import { useT, useLang, translate, DEFAULT_LANG } from '@/lib/i18n'
import { renderFields, resolveLayout, countAsyncFields } from '@/generator/ScreenGenerator'
import { Backdrop, DraggableShell } from '@/components/ui/draggable-shell'
import { ICON_BTN, CARD_STYLE, HEADER_STYLE, HEADER_TITLE, BODY_STYLE, FOOTER_STYLE } from '@/components/ui/draggable-shell-styles'

// ── Severity ───────────────────────────────────────────────────────────────────
const SEVERITY = {
  error:   { icon: 'fa-solid fa-circle-xmark',          css: '--severity-error' },
  warning: { icon: 'fa-solid fa-triangle-exclamation',  css: '--severity-warning' },
  info:    { icon: 'fa-solid fa-circle-info',           css: '--severity-info' },
  success: { icon: 'fa-solid fa-circle-check',          css: '--severity-success' },
}
const getSev = s => SEVERITY[s] ?? SEVERITY.info

// ── DialogShell — boîte modale statique ───────────────────────────────────────
// onClose : fermeture via bouton × (sémantique définie par le parent)
function DialogShell({ title, onClose, footer, wide = false, onEscape, children, zIndex = 10001 }) {
  const t = useT()
  useEffect(() => {
    const fn = e => { if (e.key === 'Escape') onEscape?.() }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  }, [onEscape])

  return (
    <>
      <Backdrop zIndex={zIndex - 1} />
      <div style={{ position: 'fixed', inset: 0, zIndex, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ ...CARD_STYLE, width: wide ? 700 : 460, maxWidth: '92vw', maxHeight: '92vh' }}>
          <div style={HEADER_STYLE}>
            <span style={HEADER_TITLE}>{title}</span>
            {onClose && (
              <button type="button" style={ICON_BTN} onClick={onClose} title={t('close')}><X size={14} /></button>
            )}
          </div>
          <div style={BODY_STYLE}>{children}</div>
          {footer && <div style={FOOTER_STYLE}>{footer}</div>}
        </div>
      </div>
    </>
  )
}

// ── Toast ──────────────────────────────────────────────────────────────────────
function Toast({ severity, message, onClose }) {
  const { icon, css } = getSev(severity)
  const color = `hsl(var(${css}))`
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      background: 'hsl(var(--popover))',
      border: '1px solid hsl(var(--border))',
      borderLeft: `4px solid ${color}`,
      borderRadius: 'var(--radius)',
      padding: '10px 12px',
      boxShadow: '0 4px 12px rgba(0,0,0,.15)',
      animation: 'tbs-slide-in .2s ease',
    }}>
      <i className={icon} style={{ color, fontSize: 15, marginTop: 1, flexShrink: 0 }} />
      <span style={{ flex: 1, fontSize: 13, color: 'hsl(var(--foreground))', lineHeight: 1.45 }} dangerouslySetInnerHTML={{ __html: message }} />
      <button type="button" onClick={onClose} style={{ ...ICON_BTN, marginTop: 1 }}><X size={12} /></button>
    </div>
  )
}

// ── MessageBox ─────────────────────────────────────────────────────────────────
function MessageBox({ severity, title, message, messageBtnOK, callbackFn, onClose, zIndex }) {
  const { icon, css } = getSev(severity)
  const color = `hsl(var(${css}))`
  const handleOK = () => { onClose(); callbackFn?.() }
  return (
    <DialogShell title={title} onClose={handleOK} onEscape={handleOK} zIndex={zIndex}
      footer={<Button onClick={handleOK}>{messageBtnOK}</Button>}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <i className={icon} style={{ color, fontSize: 20, marginTop: 1, flexShrink: 0 }} />
        <p style={{ fontSize: 13, color: 'hsl(var(--foreground))', lineHeight: 1.55, margin: 0 }} dangerouslySetInnerHTML={{ __html: message }} />
      </div>
    </DialogShell>
  )
}

// ── ConfirmationBox ────────────────────────────────────────────────────────────
function ConfirmationBox({ title, message, messageBtnOK, messageBtnCANCEL, callbackFn, onClose, zIndex }) {
  const handleOK     = () => { onClose(); callbackFn?.(true) }
  const handleCancel = () => { onClose(); callbackFn?.(false) }
  return (
    <DialogShell title={title} onClose={handleCancel} onEscape={handleCancel} zIndex={zIndex}
      footer={<>
        <Button variant="outline" onClick={handleCancel}>{messageBtnCANCEL}</Button>
        <Button onClick={handleOK}>{messageBtnOK}</Button>
      </>}>
      <p style={{ fontSize: 13, color: 'hsl(var(--foreground))', lineHeight: 1.55, margin: 0 }} dangerouslySetInnerHTML={{ __html: message }} />
    </DialogShell>
  )
}

// ── WaitingBox ─────────────────────────────────────────────────────────────────
function WaitingBox({ message, zIndex = 10001 }) {
  const t = useT()
  return (
    <>
      <Backdrop zIndex={zIndex - 1} />
      <div style={{ position: 'fixed', inset: 0, zIndex, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ ...CARD_STYLE, width: 360, maxWidth: '90vw' }}>
          <div style={HEADER_STYLE}>
            <span style={HEADER_TITLE}>{t('pleaseWait')}</span>
          </div>
          <div style={{ ...BODY_STYLE, display: 'flex', alignItems: 'center', gap: 14 }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 22, color: 'hsl(var(--primary))', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: 'hsl(var(--foreground))' }} dangerouslySetInnerHTML={{ __html: message }} />
          </div>
        </div>
      </div>
    </>
  )
}

// ── PromptSimple ───────────────────────────────────────────────────────────────
const RE_DATE  = /^\d{2}\/\d{2}\/\d{4}$/
const RE_HEURE = /^\d{2}:\d{2}$/

function PromptSimple({ type, title, label, initialValue, required, messageBtnOK, messageBtnCANCEL, callbackFn, onClose, zIndex }) {
  const t = useT()
  const isCheckbox = type === 'Checkbox'
  const [value,    setValue]   = useState(() => isCheckbox ? !!(initialValue) : (initialValue ?? ''))
  const [errorMsg, setErrorMsg] = useState(null)

  const validate = () => {
    if (!isCheckbox && required && (value === '' || value == null)) { setErrorMsg(t('requiredField')); return false }
    if (type === 'Date'  && value && !RE_DATE.test(value))          { setErrorMsg(t('invalidDateFormat')); return false }
    if (type === 'Heure' && value && !RE_HEURE.test(value))         { setErrorMsg(t('invalidTimeFormat')); return false }
    if (type === 'Number' && value !== '' && isNaN(Number(value)))  { setErrorMsg(t('invalidNumberFormat')); return false }
    return true
  }

  const handleOK     = () => { if (!validate()) return; onClose(); callbackFn?.(value) }
  const handleCancel = () => onClose()
  const clearErr     = () => { if (errorMsg) setErrorMsg(null) }

  const errBorder = errorMsg ? { borderColor: 'hsl(var(--destructive))' } : {}

  const renderInput = () => {
    if (isCheckbox) return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Checkbox checked={!!value} onCheckedChange={v => setValue(v)} />
        {label && <Label style={{ fontWeight: 400, cursor: 'pointer' }} onClick={() => setValue(v => !v)}>{label}</Label>}
      </div>
    )

    const onEnter = e => { if (e.key === 'Enter') handleOK() }

    const field = (() => {
      switch (type) {
        case 'TextArea': return <Textarea value={value} onChange={e => { setValue(e.target.value); clearErr() }} style={{ minHeight: 90, ...errBorder }} />
        case 'Date':     return <Input type="date"     value={toIso(value)}  style={{ maxWidth: 180, ...errBorder }} onChange={e => { setValue(fromIso(e.target.value)); clearErr() }} onKeyDown={onEnter} />
        case 'Heure':    return <Input type="time"     value={value}         style={{ maxWidth: 120, ...errBorder }} onChange={e => { setValue(e.target.value); clearErr() }} onKeyDown={onEnter} />
        case 'Password': return <Input type="password" value={value}         style={{ maxWidth: 300, ...errBorder }} onChange={e => { setValue(e.target.value); clearErr() }} onKeyDown={onEnter} />
        case 'Number':   return <Input type="text" inputMode="decimal" value={value} style={{ maxWidth: 200, ...errBorder }} onChange={e => { setValue(e.target.value); clearErr() }} onKeyDown={onEnter} autoFocus />
        default:         return <Input type="text"     value={value}         style={errBorder}                      onChange={e => { setValue(e.target.value); clearErr() }} onKeyDown={onEnter} autoFocus />
      }
    })()

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {label && <Label>{label}{required && <span style={{ color: 'hsl(var(--destructive))', marginLeft: 2 }}>*</span>}</Label>}
        {field}
      </div>
    )
  }

  return (
    <DialogShell title={title} onClose={handleCancel} onEscape={handleCancel} zIndex={zIndex}
      footer={<>
        <Button variant="outline" onClick={handleCancel}>{messageBtnCANCEL}</Button>
        <Button onClick={handleOK}>{messageBtnOK}</Button>
      </>}>
      {renderInput()}
      {errorMsg && <p style={{ fontSize: 12, color: 'hsl(var(--destructive))', marginTop: 6, marginBottom: 0 }}>{errorMsg}</p>}
    </DialogShell>
  )
}

// ── Helpers combo ──────────────────────────────────────────────────────────────
function toComboOption([val, lbl, sel, grp]) {
  return { code: String(val), label: String(lbl), selected: !!sel, isGroup: !!grp }
}
function buildComboGroups(opts) {
  const groups = []; let current = null
  for (const opt of opts) {
    if (opt.isGroup) { current = { groupLabel: opt.label, items: [] }; groups.push(current) }
    else { if (!current) { current = { groupLabel: null, items: [] }; groups.push(current) } current.items.push(opt) }
  }
  return groups
}

// ── PromptCombo ────────────────────────────────────────────────────────────────
function PromptCombo({ title, label, required, multiselect, items, messageBtnOK, messageBtnCANCEL, callbackFn, onClose, zIndex }) {
  const t = useT()
  const opts   = useMemo(() => (items ?? []).map(toComboOption), [items])
  const groups = useMemo(() => buildComboGroups(opts), [opts])

  const [value,    setValue]    = useState(() => { if (multiselect) return null; const pre = opts.find(o => !o.isGroup && o.selected); return pre ? pre.code : '' })
  const [selected, setSelected] = useState(() => { if (!multiselect) return null; return new Set(opts.filter(o => !o.isGroup && o.selected).map(o => o.code)) })
  const [errorMsg, setErrorMsg] = useState(null)

  const toggleOption = useCallback(code => {
    setSelected(prev => { const next = new Set(prev); next.has(code) ? next.delete(code) : next.add(code); if (next.size > 0) setErrorMsg(null); return next })
  }, [])

  const handleOK = () => {
    if (required) {
      const empty = multiselect ? selected.size === 0 : (value === '' || value == null)
      if (empty) { setErrorMsg(t('selectionRequired')); return }
    }
    onClose()
    callbackFn?.(multiselect ? [...selected] : value)
  }
  const handleCancel = () => onClose()

  const errBorder = errorMsg ? { borderColor: 'hsl(var(--destructive))' } : {}

  return (
    <DialogShell title={title} onClose={handleCancel} onEscape={handleCancel} zIndex={zIndex}
      footer={<>
        <Button variant="outline" onClick={handleCancel}>{messageBtnCANCEL}</Button>
        <Button onClick={handleOK}>{messageBtnOK}</Button>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {label && <Label>{label}{required && <span style={{ color: 'hsl(var(--destructive))', marginLeft: 2 }}>*</span>}</Label>}

        {!multiselect ? (
          <Select value={value} onValueChange={v => { setValue(v); setErrorMsg(null) }}>
            <SelectTrigger style={errBorder}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent style={{ zIndex: OVERLAY_Z_INDEX }}>
              {groups.map((g, gi) =>
                g.groupLabel != null
                  ? <SelectGroup key={gi}><SelectLabel>{g.groupLabel}</SelectLabel>{g.items.map(o => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}</SelectGroup>
                  : g.items.map(o => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)
              )}
            </SelectContent>
          </Select>
        ) : (
          <div style={{ border: `1px solid ${errorMsg ? 'hsl(var(--destructive))' : 'hsl(var(--border))'}`, borderRadius: 'var(--radius)', maxHeight: 220, overflowY: 'auto' }}>
            {opts.map((opt, i) =>
              opt.isGroup
                ? <div key={i} style={{ padding: '5px 12px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: 'hsl(var(--muted-foreground))' }}>{opt.label}</div>
                : <div key={opt.code} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', cursor: 'pointer' }} onClick={() => toggleOption(opt.code)}>
                    <Checkbox checked={selected.has(opt.code)} onCheckedChange={() => toggleOption(opt.code)} />
                    <span style={{ fontSize: 13, color: 'hsl(var(--foreground))' }}>{opt.label}</span>
                  </div>
            )}
          </div>
        )}
        {errorMsg && <p style={{ fontSize: 12, color: 'hsl(var(--destructive))', marginBottom: 0 }}>{errorMsg}</p>}
      </div>
    </DialogShell>
  )
}

// ── PromptHtmlEditor ───────────────────────────────────────────────────────────
function PromptHtmlEditor({ title, label, initialValue, required, messageBtnOK, messageBtnCANCEL, callbackFn, onClose, zIndex }) {
  const t = useT()
  const containerRef = useRef(null)
  const instanceRef  = useRef(null)
  const roRef        = useRef(null)
  const valueRef     = useRef(initialValue ?? '')
  const [errorMsg, setErrorMsg] = useState(null)

  useEffect(() => {
    if (!containerRef.current || !window.CKEDITOR) { console.warn('[PromptHtmlEditor] window.CKEDITOR non défini'); return }
    let active = true
    // Hauteur mesurée AVANT que appendTo ne modifie le DOM
    const targetH = containerRef.current.clientHeight || 380
    const inst = window.CKEDITOR.appendTo(containerRef.current, {}, valueRef.current)
    inst.on('instanceReady', () => {
      if (!active) return
      // resize(w, h) prend la hauteur TOTALE (toolbar + contenu + statusbar)
      inst.resize('100%', targetH)
      // Suit maximize et drag-resize via ResizeObserver
      roRef.current = new ResizeObserver(() => {
        if (!active || !containerRef.current) return
        const h = containerRef.current.clientHeight
        if (h > 0) inst.resize('100%', h)
      })
      roRef.current.observe(containerRef.current)
      inst.on('change', () => { if (active) { valueRef.current = inst.getData(); setErrorMsg(null) } })
    })
    instanceRef.current = inst
    return () => {
      active = false
      roRef.current?.disconnect(); roRef.current = null
      try { inst.destroy(true) } catch { /* ignore */ }
      instanceRef.current = null
    }
  }, [])

  const handleOK = () => {
    const v = instanceRef.current ? instanceRef.current.getData() : valueRef.current
    if (required && (!v || v.replace(/<[^>]*>/g, '').trim() === '')) { setErrorMsg(t('requiredField')); return }
    onClose(); callbackFn?.(v)
  }

  return (
    <DraggableShell title={title} onClose={() => onClose()} initW={760} initH={520} zIndex={zIndex}
      footer={<>
        <Button variant="outline" onClick={() => onClose()}>{messageBtnCANCEL}</Button>
        <Button onClick={handleOK}>{messageBtnOK}</Button>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
        {label && <Label>{label}{required && <span style={{ color: 'hsl(var(--destructive))', marginLeft: 2 }}>*</span>}</Label>}
        <div ref={containerRef} style={{ flex: 1, minHeight: 200, overflow: 'hidden' }} />
        {errorMsg && <p style={{ fontSize: 12, color: 'hsl(var(--destructive))', marginBottom: 0 }}>{errorMsg}</p>}
      </div>
    </DraggableShell>
  )
}

// ── PromptACEEditor ────────────────────────────────────────────────────────────
function PromptACEEditor({ aceLanguage, title, label, initialValue, required, messageBtnOK, messageBtnCANCEL, callbackFn, onClose, zIndex }) {
  const t = useT()
  const containerRef = useRef(null)
  const editorRef    = useRef(null)
  const valueRef     = useRef(initialValue ?? '')
  const [errorMsg, setErrorMsg] = useState(null)

  useEffect(() => {
    if (!containerRef.current || !window.ace) { console.warn('[PromptACEEditor] window.ace non défini'); return }
    let active = true
    const editor = window.ace.edit(containerRef.current)
    if (aceLanguage) editor.session.setMode(`ace/mode/${aceLanguage}`)
    editor.setValue(valueRef.current, -1)
    editor.on('change', () => { if (active) { valueRef.current = editor.getValue(); setErrorMsg(null) } })
    editorRef.current = editor
    return () => { active = false; editor.destroy(); editorRef.current = null }
  }, [aceLanguage])

  const handleOK = () => {
    const v = editorRef.current ? editorRef.current.getValue() : valueRef.current
    if (required && (!v || v.trim() === '')) { setErrorMsg(t('requiredField')); return }
    onClose(); callbackFn?.(v)
  }

  return (
    <DraggableShell title={title} onClose={() => onClose()} initW={760} initH={520} zIndex={zIndex}
      footer={<>
        <Button variant="outline" onClick={() => onClose()}>{messageBtnCANCEL}</Button>
        <Button onClick={handleOK}>{messageBtnOK}</Button>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
        {label && <Label>{label}{required && <span style={{ color: 'hsl(var(--destructive))', marginLeft: 2 }}>*</span>}</Label>}
        <div ref={containerRef} style={{ flex: 1, minHeight: 260, border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)' }} />
        {errorMsg && <p style={{ fontSize: 12, color: 'hsl(var(--destructive))', marginBottom: 0 }}>{errorMsg}</p>}
      </div>
    </DraggableShell>
  )
}

// ── PopupInplace ───────────────────────────────────────────────────────────────
function PopupInplace({ title, width, height, url, params, method, maximized: initMax, beforeCloseCallback, onClose, zIndex }) {
  const [iframeId] = useState(() => `tbs-iframe-${generateId().slice(0, 8)}`)
  const formRef  = useRef(null)
  const submittedRef = useRef(false)
  const handleClose = () => { beforeCloseCallback?.(); onClose() }

  // POST si method='POST' explicitement, ou si params fournis sans method (rétrocompatibilité)
  const isPost = method === 'POST' || (!method && !!params)

  // Guard contre le double-submit du StrictMode (mount/unmount/remount en dev) : sans lui,
  // la même requête POST serait envoyée deux fois vers l'iframe cible.
  useEffect(() => {
    if (submittedRef.current) return
    submittedRef.current = true
    if (formRef.current) formRef.current.submit()
  }, [])

  const paramEntries = isPost && params
    ? params.split('&').filter(Boolean).map(p => { const [k, ...rest] = p.split('='); return [decodeURIComponent(k), decodeURIComponent(rest.join('='))] })
    : []

  // GET : params ajoutés en query string (en respectant un éventuel '?' déjà présent dans url) ;
  // POST : soumission via form caché ciblant l'iframe
  const getUrl = !isPost ? (params ? `${url}${url.includes('?') ? '&' : '?'}${params}` : url) : undefined

  return (
    <DraggableShell title={title} onClose={handleClose} initW={width} initH={height} initMaximized={!!initMax} zIndex={zIndex}
      bodyStyle={{ padding: 0 }}>
      <iframe id={iframeId} name={iframeId}
        src={getUrl}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
      {isPost && (
        <form ref={formRef} method="POST" action={url} target={iframeId} style={{ display: 'none' }}>
          {paramEntries.map(([k, v], i) => <input key={i} type="hidden" name={k} value={v} readOnly />)}
        </form>
      )}
    </DraggableShell>
  )
}

// ── ModalDialogBox ─────────────────────────────────────────────────────────────
// Parcourt récursivement le descripteur JSON pour collecter tous les ids de champs
function collectFieldIds(fields) {
  if (!fields) return []
  const ids = []
  for (const field of Object.values(fields)) {
    ids.push(field.id)
    if (field.fields) ids.push(...collectFieldIds(field.fields))
    if (field.elements) {
      for (const el of (field.elements ?? [])) ids.push(...collectFieldIds(el.fields))
    }
  }
  return ids
}

function ModalDialogBox({
  title, width, height, json, maximized: initMax,
  eventCallback, dynaComboLoader, gridLoader, treeLoader, gridDragDropCallback, treeDragDropCallback,
  beforeCloseCallback, onClose, zIndex,
}) {
  const fieldIds = useMemo(() => collectFieldIds(json?.fields ?? {}), [json])

  const handleEvent = useCallback((id, eventType, ...args) =>
    safeInvoke(eventCallback || window.onTbsEventCallback, id, eventType, ...args)
  , [eventCallback])

  const loaders = useMemo(() => ({ dynaComboLoader, gridLoader, treeLoader, gridDragDropCallback, treeDragDropCallback }),
    [dynaComboLoader, gridLoader, treeLoader, gridDragDropCallback, treeDragDropCallback])

  // Émission de `onLoad` sur json.id — même mécanisme que ScreenGenerator (attend la fin du
  // chargement des éventuels tableaux/arbres imbriqués via countAsyncFields/onDataLoaded), et
  // mêmes refs figées à l'instanciation (jsonIdRef/handleEventRef) : valide ici aussi car chaque
  // ouverture de modalDialogBox instancie un nouveau ModalDialogBox — prepareForGroup(3), appelé
  // avant openModal(), ferme systématiquement (donc démonte) toute modalDialogBox de groupe <= 3
  // déjà affichée (voir modalDialogBox() plus bas et la règle des groupes) ; `key={__z}` posé au
  // rendu (UserMessages) le garantit explicitement.
  const pendingRef      = useRef(countAsyncFields(json?.fields ?? {}))
  const firedRef        = useRef(false)
  const jsonIdRef       = useRef(json?.id)
  const handleEventRef  = useRef(handleEvent)
  useLayoutEffect(() => { handleEventRef.current = handleEvent })

  const onDataLoaded = useCallback(() => {
    if (firedRef.current) return
    pendingRef.current = Math.max(0, pendingRef.current - 1)
    if (pendingRef.current === 0) {
      firedRef.current = true
      handleEventRef.current(jsonIdRef.current, 'onLoad')
    }
  }, [])

  useEffect(() => {
    if (pendingRef.current === 0 && !firedRef.current) {
      firedRef.current = true
      handleEventRef.current(jsonIdRef.current, 'onLoad')
    }
  }, [])

  // Supprime immédiatement et synchronement toutes les entrées du dialogue de tbsComponents.
  // Nécessaire car le cleanup React (useEffect) est asynchrone : si un event déclenche un submit
  // du formulaire principal, les champs du dialogue seraient encore présents. Même traitement,
  // dupliqué, dans hidePopups() (UserMessages) quand la fermeture est déclenchée depuis l'extérieur
  // (l'unmount ne passe alors pas par handleClose ci-dessous).
  const purgeComponents = useCallback(() => {
    for (const id of fieldIds) delete window.tbsComponents?.[id]
  }, [fieldIds])

  const handleClose = () => {
    purgeComponents()
    beforeCloseCallback?.()
    onClose()
  }

  // eslint-disable-next-line react-hooks/refs
  const dialogFields = json && renderFields(json.fields, handleEvent, resolveLayout(json), onDataLoaded, loaders)

  return (
    <DraggableShell title={title} onClose={handleClose} initW={width} initH={height} initMaximized={!!initMax} zIndex={zIndex}>
      {dialogFields}
    </DraggableShell>
  )
}

// ── MessagesFrame ──────────────────────────────────────────────────────────────
function MessagesFrame({ messages, visible, onHide }) {
  const t = useT()
  if (!visible) return null
  return (
    <div style={{
      position: 'relative',
      background: 'hsl(var(--messages-bg))',
      borderBottom: '2px solid hsl(var(--border))',
      maxHeight: 220,
      overflowY: messages.length > 10 ? 'auto' : 'visible',
    }}>
      {/* Bouton masquer — coin haut-droit */}
      <button type="button" style={{ ...ICON_BTN, position: 'absolute', top: 4, right: 6, zIndex: 1 }}
        onClick={onHide} title={t('hide')}>
        <X size={12} />
      </button>

      {/* Messages */}
      {messages.map(m => {
        const { icon, css } = getSev(m.severity)
        const color = `hsl(var(${css}))`
        return (
          <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '5px 32px 5px 16px', borderBottom: '1px solid hsl(var(--border))' }}>
            <i className={icon} style={{ color, fontSize: 13, marginTop: 2, flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'hsl(var(--foreground))', lineHeight: 1.4 }} dangerouslySetInnerHTML={{ __html: m.message }} />
          </div>
        )
      })}
    </div>
  )
}

// ── ChatWidget — bouton flottant bas-droite + panneau de discussion ────────────
// Historique de conversation purement local (state React) — pas de persistance : une nouvelle
// conversation démarre à chaque remontage de l'écran (reload complet), et peut aussi se terminer
// en cours de route sans reload (ex. bonne réponse obtenue) — dans ce cas c'est au code métier de
// signaler la fin via le `handler` de sa réponse (cf. addChatResponse plus bas), pas à ce composant
// de le déduire. Sous le zIndex des popups/modales (BASE_Z_INDEX) : un message/confirmation reste
// prioritaire sur le chat.
const CHAT_Z_INDEX = 9000

// Dictée vocale (Web Speech API, `SpeechRecognition`/`webkitSpeechRecognition` — non standardisée,
// Chrome/Edge/Safari uniquement, absente de Firefox) : bouton micro masqué entièrement si l'API est
// indisponible (cf. SpeechRecognitionCtor dans ChatWidget), plutôt qu'affiché inopérant. `lang`
// aligné sur la langue courante du chrome React (`useLang()`) — repli `fr-FR` si non couverte.
const SPEECH_LANG = { fr: 'fr-FR', en: 'en-US', es: 'es-ES', de: 'de-DE', it: 'it-IT', ar: 'ar-SA' }

// SpeechSynthesis (lecture à voix haute) — contrairement à SpeechRecognition (dictée), largement
// supportée (Chrome/Edge/Firefox/Safari) : pas besoin de masquer le bouton par défaut, juste de
// vérifier sa présence (environnements headless/anciens navigateurs).
const SpeechSynthesisAvailable = typeof window !== 'undefined' && !!window.speechSynthesis

// Bouton discret de la barre d'actions en filigrane (copier/lire/apprécier) — petit, semi-
// transparent, couleur alignée sur le texte de la bulle (blanc translucide côté question, gris
// muted côté réponse) pour rester visible sans concurrencer le texte.
function bubbleActionBtnStyle(isUser, active) {
  return {
    background: 'none', border: 'none', cursor: 'pointer', padding: 3,
    display: 'flex', alignItems: 'center', borderRadius: 3,
    color: active ? (isUser ? '#fff' : 'hsl(var(--primary))') : (isUser ? 'hsl(var(--primary-foreground))' : 'hsl(var(--muted-foreground))'),
    opacity: active ? 1 : 0.6,
  }
}

function ChatBubble({ msg, speaking, onSpeak }) {
  const t = useT()
  const isUser = msg.role === 'user'
  const [copied, setCopied] = useState(false)
  const [rated,  setRated]  = useState(null) // true (pouce haut) | false (pouce bas) | null
  const copyTimerRef = useRef(null)
  useEffect(() => () => clearTimeout(copyTimerRef.current), [])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(msg.text ?? '')
      setCopied(true)
      clearTimeout(copyTimerRef.current)
      copyTimerRef.current = setTimeout(() => setCopied(false), 1500)
    } catch (e) {
      console.error('[ChatBubble] Échec de la copie dans le presse-papiers :', e)
    }
  }

  // Signature exacte demandée : tbsOnUserChatNotice(appreciated) — un seul booléen, pas d'id de
  // message/handler. `rated` ne sert qu'au surlignage visuel local du pouce cliqué.
  const handleRate = appreciated => {
    setRated(appreciated)
    safeInvoke(window.tbsOnUserChatNotice, appreciated)
  }

  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      {/* Marge fixe en pixels (pas un pourcentage) — en mode agrandi (panneau ≈ plein viewport),
          un maxWidth en % laisserait des lignes bien trop longues pour rester lisibles ; ici la
          bulle "question" (ancrée à droite) ne s'approche jamais à moins de 100px du bord gauche,
          et symétriquement la bulle "réponse" (ancrée à gauche) jamais à moins de 100px du bord
          droit — quelle que soit la largeur du panneau. */}
      <div style={{
        // width:100% + maxWidth — le cadre occupe systématiquement toute la largeur disponible
        // (jusqu'à cette marge de 100px), au lieu de se réduire au contenu (shrink-to-fit, défaut
        // d'un flex item sans width explicite) : largeur uniforme pour toutes les bulles, cadre
        // bien identifiable même pour un message court.
        width: '100%',
        maxWidth: 'calc(100% - 100px)',
        // minWidth:0 — un flex item a un min-width:auto implicite (pas 0) : sans ce reset, un
        // token non cassable (URL/nom de fichier long, sans espace) empêcherait la bulle de
        // rétrécir en dessous de sa largeur intrinsèque, dépassant alors maxWidth. overflowWrap
        // force la coupure de ce genre de token plutôt que de laisser déborder.
        minWidth: 0,
        overflowWrap: 'break-word',
        background: isUser ? 'hsl(var(--primary))' : 'hsl(var(--muted))',
        color: isUser ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
        borderRadius: 'var(--radius)',
        padding: '8px 11px',
        fontSize: 13,
        lineHeight: 1.45,
        whiteSpace: 'pre-wrap',
      }}>
        {msg.text}
        {msg.files?.length > 0 && (
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {msg.files.map((f, i) => (
              <span key={i} style={{ fontSize: 11, opacity: .85, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Paperclip size={11} />{f.name}
              </span>
            ))}
          </div>
        )}

        {/* Barre d'actions en filigrane — copier (les deux), lire à voix haute (les deux),
            apprécier (réponse uniquement, cf. demande). Séparée du texte par une fine bordure,
            dans une teinte translucide alignée sur la couleur du texte de la bulle. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 2, marginTop: 6, paddingTop: 5,
          borderTop: `1px solid ${isUser ? 'rgba(255,255,255,.3)' : 'hsl(var(--border))'}`,
        }}>
          <button type="button" onClick={handleCopy} title={t('chatCopy')} style={bubbleActionBtnStyle(isUser, copied)}>
            {copied ? <Check size={12} /> : <Copy size={12} />}
          </button>
          {SpeechSynthesisAvailable && (
            <button type="button" onClick={onSpeak} title={speaking ? t('chatStopSpeak') : t('chatSpeak')} style={bubbleActionBtnStyle(isUser, speaking)}>
              {speaking ? <VolumeX size={12} /> : <Volume2 size={12} />}
            </button>
          )}
          {!isUser && (
            <>
              <button type="button" onClick={() => handleRate(true)} title={t('chatThumbsUp')} style={bubbleActionBtnStyle(isUser, rated === true)}>
                <ThumbsUp size={12} />
              </button>
              <button type="button" onClick={() => handleRate(false)} title={t('chatThumbsDown')} style={bubbleActionBtnStyle(isUser, rated === false)}>
                <ThumbsDown size={12} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function ChatWidget({ open, onToggle, onHide, history, sending, onSend }) {
  const t    = useT()
  const lang = useLang()
  const [text, setText]   = useState('')
  const [files, setFiles] = useState([])
  const [listening, setListening] = useState(false)
  const [micError, setMicError]   = useState(null)
  const [maximized, setMaximized] = useState(false)
  // Id du message en cours de lecture à voix haute (un seul à la fois, cf. handleSpeak) — state
  // partagé ici plutôt que local à chaque ChatBubble : window.speechSynthesis est une ressource
  // globale unique, et démarrer une 2e lecture doit interrompre la 1re, pas les superposer.
  const [speakingId, setSpeakingId] = useState(null)
  const listRef         = useRef(null)
  const fileInputRef    = useRef(null)
  const recognitionRef  = useRef(null)
  // Texte déjà présent dans le champ au moment où la dictée démarre — chaque onresult réécrit le
  // texte en entier (base + transcript courant, final ou intermédiaire), jamais un simple append
  // cumulatif (qui dupliquerait le contenu à chaque événement intermédiaire, interimResults:true
  // renvoyant systématiquement l'historique complet de la phrase en cours, pas seulement le delta).
  const baseTextRef     = useRef('')

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight
  }, [history, sending])

  // Arrête toute dictée en cours si le panneau se ferme/le composant se démonte — sans ça, la
  // reconnaissance continuerait en arrière-plan (rien à l'écran pour l'arrêter) jusqu'à son propre
  // timeout navigateur. Même raisonnement pour la lecture à voix haute (cancel() inconditionnel,
  // sans dépendre de speakingId — la ressource est globale, pas besoin de savoir qui lisait).
  useEffect(() => () => {
    recognitionRef.current?.stop()
    window.speechSynthesis?.cancel()
  }, [])

  // Lecture à voix haute d'une bulle (question ou réponse) — un seul énoncé actif à la fois :
  // redémarrer sur le même id coupe la lecture (toggle) ; sur un autre id, coupe l'actuelle avant
  // de démarrer la nouvelle (cancel() global, speechSynthesis ne sait lire qu'un énoncé à la fois).
  const handleSpeak = (id, text) => {
    if (!window.speechSynthesis) return
    window.speechSynthesis.cancel()
    if (speakingId === id) { setSpeakingId(null); return }
    const utterance = new SpeechSynthesisUtterance(text ?? '')
    utterance.lang = SPEECH_LANG[lang] ?? 'fr-FR'
    utterance.onend   = () => setSpeakingId(null)
    utterance.onerror = () => setSpeakingId(null)
    window.speechSynthesis.speak(utterance)
    setSpeakingId(id)
  }

  const handleSend = () => {
    if (!text.trim() && files.length === 0) return
    onSend(text.trim(), files)
    setText('')
    setFiles([])
  }

  const handlePickFiles = e => {
    setFiles(prev => [...prev, ...Array.from(e.target.files ?? [])])
    e.target.value = ''
  }

  const removeFile = idx => setFiles(prev => prev.filter((_, i) => i !== idx))

  const SpeechRecognitionCtor = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition)

  // `continuous:true` — la Web Speech API n'expose aucun réglage de timeout de silence (pas de
  // `recognition.silenceTimeout`/équivalent standard) : avec `continuous:false`, Chrome applique sa
  // propre heuristique de fin de phrase et coupe dès qu'il détecte un blanc, souvent trop tôt sur
  // une dictée avec pauses naturelles. `continuous:true` fait l'inverse — la reconnaissance continue
  // across les blancs jusqu'à `recognition.stop()` (re-clic sur le micro) ou une erreur ; c'est le
  // seul levier disponible côté navigateur pour ce comportement.
  // `interimResults:true` : sans lui, certaines versions de Chrome ne délivrent jamais le résultat
  // final sur un énoncé court (observé — onresult n'est alors jamais appelé, rien n'apparaît dans
  // le champ, sans erreur visible). En continu, `e.results` grandit au fil de la session (chaque
  // segment marqué `isFinal` une fois confirmé, cf. `onresult`) — `finalTranscript` accumule les
  // segments déjà confirmés, `e.resultIndex` évite de les retraiter à chaque événement.
  const toggleListening = () => {
    if (!SpeechRecognitionCtor) return
    if (listening) { recognitionRef.current?.stop(); return }
    setMicError(null)
    baseTextRef.current = text
    const recognition = new SpeechRecognitionCtor()
    recognition.lang = SPEECH_LANG[lang] ?? 'fr-FR'
    recognition.interimResults = true
    recognition.continuous = true
    let finalTranscript = ''
    recognition.onresult = e => {
      let interim = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i]
        if (r.isFinal) finalTranscript += r[0].transcript + ' '
        else interim += r[0].transcript
      }
      const base = baseTextRef.current.trim()
      const combined = (finalTranscript + interim).trim()
      setText(combined ? (base ? base + ' ' : '') + combined : baseTextRef.current)
    }
    // error.error : code standard (`not-allowed`, `no-speech`, `audio-capture`, `network`...) —
    // affiché tel quel (cf. chatVoiceError) faute de traduction exhaustive de tous les codes
    // possibles ; suffisant pour diagnostiquer (permission refusée, pas de micro, hors-ligne...).
    recognition.onerror = e => { setListening(false); setMicError(e.error) }
    recognition.onend   = () => setListening(false)
    recognitionRef.current = recognition
    try {
      recognition.start()
      setListening(true)
    } catch {
      setMicError('start-failed')
    }
  }

  return (
    <>
      {open && (
        <div style={{
          position: 'fixed',
          display: 'flex', flexDirection: 'column',
          background: 'hsl(var(--popover))',
          border: '1px solid hsl(var(--border))',
          boxShadow: '0 8px 28px rgba(0,0,0,.2)',
          zIndex: CHAT_Z_INDEX,
          // Maximisé : même convention visuelle que DraggableShell (plein viewport, coins carrés) —
          // pas de drag/resize ici (panneau toujours ancré, pas de mode "fenêtre libre"), juste ce
          // 2e palier de taille.
          ...(maximized
            ? { inset: 0, width: '100vw', height: '100vh', borderRadius: 0 }
            : { right: 20, bottom: 86, width: 340, maxHeight: 480, borderRadius: 'var(--radius)' }),
        }}>
          <div style={HEADER_STYLE}>
            <span style={HEADER_TITLE}>{t('chatTitle')}</span>
            <button type="button" style={ICON_BTN} onClick={onHide} title={t('chatHide')}><EyeOff size={14} /></button>
            <button type="button" style={ICON_BTN} onClick={() => setMaximized(m => !m)} title={maximized ? t('chatRestore') : t('chatMaximize')}>
              <i className={maximized ? 'fa-regular fa-window-restore' : 'fa-regular fa-window-maximize'} style={{ fontSize: 13 }} aria-hidden="true" />
            </button>
            <button type="button" style={ICON_BTN} onClick={onToggle} title={t('close')}><X size={14} /></button>
          </div>
          <div ref={listRef} style={{ flex: 1, minHeight: 200, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {history.map(m => (
              <ChatBubble key={m.id} msg={m} speaking={speakingId === m.id} onSpeak={() => handleSpeak(m.id, m.text)} />
            ))}
            {sending && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ background: 'hsl(var(--muted))', borderRadius: 'var(--radius)', padding: '8px 11px' }}>
                  <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))' }} />
                </div>
              </div>
            )}
          </div>
          {files.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '0 12px 8px' }}>
              {files.map((f, i) => (
                <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, background: 'hsl(var(--muted))', borderRadius: 999, padding: '2px 8px' }}>
                  {f.name}
                  <button type="button" onClick={() => removeFile(i)} style={{ border: 'none', background: 'none', cursor: 'pointer', display: 'flex' }}><X size={10} /></button>
                </span>
              ))}
            </div>
          )}
          {micError && (
            <p style={{ fontSize: 11, color: 'hsl(var(--destructive))', margin: '0 12px 6px' }}>
              {t('chatVoiceError', { error: micError })}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, padding: 10, borderTop: '1px solid hsl(var(--border))' }}>
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handlePickFiles} />
            <button type="button" title={t('chatAttach')} onClick={() => fileInputRef.current?.click()} style={{ ...ICON_BTN, flexShrink: 0 }}>
              <Paperclip size={16} />
            </button>
            {SpeechRecognitionCtor && (
              <button type="button" title={t('chatVoice')} onClick={toggleListening}
                style={{
                  ...ICON_BTN, flexShrink: 0,
                  color: listening ? 'hsl(var(--destructive))' : undefined,
                }}>
                <Mic size={16} />
              </button>
            )}
            <Textarea value={text} onChange={e => setText(e.target.value)} placeholder={t('chatPlaceholder')}
              style={{ minHeight: 34, maxHeight: 90, resize: 'none', fontSize: 13 }}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }} />
            <Button size="sm" title={t('chatSend')} onClick={handleSend} style={{ flexShrink: 0 }}>
              <Send size={14} />
            </Button>
          </div>
        </div>
      )}

      {/* Masqué quand le panneau est agrandi : chevaucherait le bouton "Envoyer" (même coin bas-
          droite que le panneau maximisé) — le × de l'en-tête suffit alors à fermer/restaurer. */}
      {!(open && maximized) && (
      <button type="button" onClick={onToggle} title={t('chatButtonLabel')}
        style={{
          position: 'fixed', right: 20, bottom: 20, width: 52, height: 52, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))',
          border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,.25)',
          zIndex: CHAT_Z_INDEX,
        }}>
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
      )}
    </>
  )
}

// ── Groupes de popups (priorité d'affichage) ───────────────────────────────────
// groupe 1 < groupe 2 < groupe 3 : ouvrir une popup d'un groupe cache toute popup active de
// groupe inférieur ou égal (toast excepté : les toasts s'empilent entre eux, mais un groupe 1
// "modal" (messageBox/waitingBox/confirmationBox) cache/est caché par un toast comme le reste du
// groupe 1). `toast` n'a pas d'entrée ici (n'occupe pas le slot `modal`, géré séparément).
const MODAL_GROUP = {
  messageBox: 1, waitingBox: 1, confirmationBox: 1,
  promptSimple: 2, promptCombo: 2, promptHtmlEditor: 2, promptACEEditor: 2,
  popupInplace: 3, modalDialogBox: 3,
}

// zIndex de base des 3 emplacements — le dernier ouvert (n'importe quel groupe) doit passer
// au-dessus des autres popups déjà actives (ex. confirmationBox groupe 1 ouvert par-dessus un
// modalDialogBox groupe 3 : il doit être cliquable, donc visuellement au-dessus, même si son
// groupe est inférieur). Un palier fixe par groupe ne conviendrait pas ici. `allocZ()` alloue
// donc un zIndex croissant à chaque ouverture, indépendamment du groupe — voir openModal().
const BASE_Z_INDEX = 10101 // au-dessus du zIndex par défaut (10001) utilisé hors groupes (preview MultiFileField)

// ── UserMessages — composant principal ────────────────────────────────────────
const UserMessages = forwardRef(function UserMessages({ lang = DEFAULT_LANG, initialChatVisible = null }, ref) {
  const [toasts,       setToasts]       = useState([])
  const [chatVisible, setChatVisible]   = useState(false)
  const [chatOpen,    setChatOpen]      = useState(false)
  const [chatHistory, setChatHistory]   = useState([])
  const [chatSending, setChatSending]   = useState(false)
  // Mémorisé côté écran (pas en state React — lu par getChatHandler(), appelé depuis du code
  // métier hors cycle de rendu, juste avant son propre appel au backend) : même raisonnement que
  // les refs synchrones documentées dans CLAUDE.md (convention tbsComponents).
  const chatHandlerRef = useRef(null)
  // Même raisonnement que chatHandlerRef ci-dessus : toggleChatButton() (plus bas) a besoin de lire
  // la visibilité courante de façon synchrone pour la basculer — chatVisible (state) seul s'y
  // prêterait mal (fermeture figée à la 1re exécution de useImperativeHandle, cf. CLAUDE.md). Le
  // state reste la source pour le rendu ({chatVisible && <ChatWidget/>} plus bas) ; la ref ne sert
  // qu'aux lectures synchrones hors rendu. setChatVisibleSynced tient les deux à jour ensemble.
  const chatVisibleRef = useRef(false)
  const setChatVisibleSynced = useCallback(next => {
    const value = typeof next === 'function' ? next(chatVisibleRef.current) : next
    chatVisibleRef.current = value
    setChatVisible(value)
  }, [])
  // Filet de sécurité pour chatVisibleRef : lire/écrire une ref pendant le rendu est interdit
  // (react-hooks/refs), donc le bloc « adjusting state » ci-dessous ne peut pas appeler
  // setChatVisibleSynced (qui touche chatVisibleRef) — seulement setChatVisible (state). Cet effet
  // garde la ref alignée sur le state après coup, y compris pour cette initialisation.
  useLayoutEffect(() => { chatVisibleRef.current = chatVisible }, [chatVisible])
  // Pattern « adjusting state », identique à chromeInitialized dans App.jsx : applique la
  // visibilité initiale une seule fois, dès que __TBS_CHAT__ est chargé (jamais synchrone au 1er
  // rendu).
  const [chatInitDone, setChatInitDone] = useState(false)
  if (!chatInitDone && initialChatVisible != null) {
    setChatInitDone(true)
    setChatVisible(initialChatVisible)
  }
  // Un emplacement par groupe : les 3 peuvent être actifs simultanément (ex. confirmationBox
  // affiché par-dessus un modalDialogBox déjà ouvert, sans le cacher — voir MODAL_GROUP/règles).
  const [modal1, setModal1] = useState(null)
  const [modal2, setModal2] = useState(null)
  const [modal3, setModal3] = useState(null)
  const [messages,     setMessages]     = useState([])
  const [frameVisible, setFrameVisible] = useState(false)
  const waitTimerRef                    = useRef(null)
  const langRef                         = useRef(lang)
  // modalSlotsRef reflète {1,2,3} de façon synchrone — nécessaire pour que hidePopups()/
  // closeModalIfGroupAtMost() (appelés de façon imperative, hors cycle de rendu React) sachent
  // quelle(s) modale(s) sont ouvertes et appellent leur éventuel beforeCloseCallback avant fermeture.
  const modalSlotsRef                   = useRef({ 1: null, 2: null, 3: null })
  // Compteur de premier-plan : incrémenté à chaque ouverture (tous groupes confondus), pour que la
  // dernière popup ouverte soit toujours au-dessus des autres actives, indépendamment de son
  // groupe (voir BASE_Z_INDEX) — son propre backdrop (zIndex-1) passe alors aussi au-dessus des
  // popups déjà actives, qui apparaissent visuellement assombries/inactives, comme attendu.
  const zCounterRef                     = useRef(0)
  useEffect(() => { langRef.current = lang }, [lang])

  const removeToast = useCallback(id => setToasts(prev => prev.filter(t => t.id !== id)), [])

  const SET_SLOT = { 1: setModal1, 2: setModal2, 3: setModal3 }
  const setSlot = useCallback((group, m) => {
    modalSlotsRef.current[group] = m
    SET_SLOT[group](m)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // flushSync : modalDialogBox monte les champs de `json.fields` via renderFields() — ils
  // s'enregistrent dans tbsComponents à leur montage (useEffect de chaque composant). Sans flush
  // synchrone, un traitement métier appelant tbsComponents[xxx] juste après modalDialogBox()
  // (même traitement) ne les trouverait pas encore (même raison que CanvasField.addElements).
  // Sans incidence sur les autres popups (messageBox, popupInplace...), qui ne portent pas de
  // champs dynamiques.
  const openModal = useCallback(m => {
    zCounterRef.current += 2
    flushSync(() => setSlot(MODAL_GROUP[m._modal], { ...m, __z: BASE_Z_INDEX + zCounterRef.current }))
  }, [setSlot])

  // Ferme chaque emplacement actif de groupe <= maxGroup (no-op si rien d'actif dans cette plage).
  // Purge tbsComponents pour modalDialogBox — identique au bouton × de ModalDialogBox (fermeture
  // déclenchée ici depuis l'extérieur, sans passer par son propre handler) — puis
  // beforeCloseCallback, dans tous les cas de fermeture.
  const closeModalIfGroupAtMost = useCallback(maxGroup => {
    for (let g = 1; g <= maxGroup; g++) {
      const m = modalSlotsRef.current[g]
      if (!m) continue
      if (m._modal === 'modalDialogBox') {
        for (const id of collectFieldIds(m.json?.fields ?? {})) delete window.tbsComponents?.[id]
      }
      m.beforeCloseCallback?.()
      setSlot(g, null)
    }
  }, [setSlot])

  const closeModal1 = useCallback(() => setSlot(1, null), [setSlot])
  const closeModal2 = useCallback(() => setSlot(2, null), [setSlot])
  const closeModal3 = useCallback(() => setSlot(3, null), [setSlot])

  // À appeler avant l'ouverture de toute popup de groupe >= 1 (donc jamais par toast(), qui a sa
  // propre règle : cache le groupe 1 sans s'empiler-fermer lui-même) : annule un waitingBox en
  // attente, vide les toasts (membres du groupe 1) et cache la modale active de groupe <= newGroup.
  const prepareForGroup = useCallback(newGroup => {
    clearTimeout(waitTimerRef.current)
    setToasts([])
    closeModalIfGroupAtMost(newGroup)
  }, [closeModalIfGroupAtMost])

  const defOK     = () => translate(langRef.current, 'ok')
  const defCancel = () => translate(langRef.current, 'cancel')
  const withBtns  = useCallback(p => ({
    ...p,
    messageBtnOK:     p.messageBtnOK     || defOK(),
    messageBtnCANCEL: p.messageBtnCANCEL || defCancel(),
  }), [])

  // Déclenché par ChatWidget au clic "Envoyer" — bulle utilisateur affichée immédiatement
  // (optimiste), puis onTbsChatMessage(message, files) prend le relais côté métier : c'est lui qui
  // appelle le backend (en promise) avec le handler courant (getChatHandler()), et c'est
  // addChatResponse() (ci-dessous) qui affichera la réponse une fois celle-ci obtenue — aucun
  // retour direct attendu ici (fire-and-forget, comme les autres global callbacks safeInvoke()).
  const handleSendChatMessage = useCallback((text, files) => {
    setChatHistory(prev => [...prev, { id: generateId(), role: 'user', text, files }])
    setChatSending(true)
    safeInvoke(window.onTbsChatMessage, text, files)
  }, [])

  // Bouton "masquer" dans l'en-tête du panneau (ChatWidget) — même effet que hideChatButton()
  // (API tbsUserMessages), accessible directement depuis l'écran sans repasser par le métier.
  const handleHideChatButton = useCallback(() => {
    setChatVisibleSynced(false)
    setChatOpen(false)
  }, [setChatVisibleSynced])

  useImperativeHandle(ref, () => ({
    toast({ severity, message, duration }) {
      // groupe 1 : cache messageBox/waitingBox/confirmationBox actif, mais les toasts entre eux
      // continuent de s'empiler (pas de setToasts([]) ici).
      clearTimeout(waitTimerRef.current)
      closeModalIfGroupAtMost(1)
      const id = generateId()
      setToasts(prev => [...prev, { id, severity, message }])
      setTimeout(() => removeToast(id), duration)
    },
    messageBox(params)      { prepareForGroup(1); openModal({ _modal: 'messageBox',      ...params, messageBtnOK: params.messageBtnOK || defOK() }) },
    confirmationBox(params) { prepareForGroup(1); openModal({ _modal: 'confirmationBox', ...withBtns(params) }) },
    waitingBox({ message, triggeringTime }) {
      clearTimeout(waitTimerRef.current)
      // prepareForGroup() différé jusqu'à l'affichage effectif (après triggeringTime) : tant que
      // le délai court, rien n'est encore visible donc rien à cacher.
      waitTimerRef.current = setTimeout(() => {
        setToasts([])
        closeModalIfGroupAtMost(1)
        openModal({ _modal: 'waitingBox', message })
      }, triggeringTime)
    },
    hideWaitingBox() {
      // Annule aussi un waitingBox en attente (triggeringTime pas encore écoulé, rien d'affiché
      // pour l'instant) ; ne referme le slot groupe 1 que s'il porte bien un waitingBox — un
      // messageBox/confirmationBox affiché entre-temps dans ce même emplacement n'est pas concerné.
      clearTimeout(waitTimerRef.current)
      if (modalSlotsRef.current[1]?._modal === 'waitingBox') setSlot(1, null)
    },
    promptSimple(params)     { prepareForGroup(2); openModal({ _modal: 'promptSimple',     ...withBtns(params) }) },
    promptCombo(params)      { prepareForGroup(2); openModal({ _modal: 'promptCombo',      ...withBtns(params) }) },
    promptHtmlEditor(params) { prepareForGroup(2); openModal({ _modal: 'promptHtmlEditor', ...withBtns(params) }) },
    promptACEEditor(params)  { prepareForGroup(2); openModal({ _modal: 'promptACEEditor',  ...withBtns(params) }) },
    popupInplace(params)     { prepareForGroup(3); openModal({ _modal: 'popupInplace',     ...params }) },
    modalDialogBox(params)   { prepareForGroup(3); openModal({ _modal: 'modalDialogBox',   ...params }) },
    hidePopups() {
      clearTimeout(waitTimerRef.current)
      setToasts([])
      closeModalIfGroupAtMost(3)
    },
    addMessage({ severity, message }) {
      const id = generateId()
      setMessages(prev => [...prev, { id, severity, message }])
      setFrameVisible(true)
    },
    clearMessagesFrame()  { setMessages([]) },
    showMessagesFrame()   { setFrameVisible(true) },
    hideMessagesFrame()   { setFrameVisible(false) },

    showChatButton() { setChatVisibleSynced(true) },
    hideChatButton() { setChatVisibleSynced(false); setChatOpen(false) },
    // Bascule la visibilité du bouton flottant — déclenché par le bouton toolbar type "ChatState"
    // (Toolbar.jsx), lui-même sans connaissance de l'état courant (appel à l'aveugle). Referme
    // aussi le panneau quand on masque, même raisonnement que hideChatButton().
    toggleChatButton() {
      const next = !chatVisibleRef.current
      setChatVisibleSynced(next)
      if (!next) setChatOpen(false)
    },
    // Lu par le code métier (onTbsChatMessage) juste avant d'appeler son propre backend — `null`
    // pour une conversation qui n'a encore jamais reçu de réponse (1er message) ou qui vient de se
    // terminer (cf. addChatResponse ci-dessous).
    getChatHandler() { return chatHandlerRef.current },
    // `json` : { handler, response, ...reste selon le contexte métier, ignoré ici }. `handler`
    // absent/null → la conversation est considérée terminée : le prochain message en démarrera
    // une nouvelle (getChatHandler() retombe à null), sans que ce composant n'ait à en décider —
    // c'est au backend de le signaler ainsi.
    addChatResponse(json) {
      const { handler, response } = json ?? {}
      chatHandlerRef.current = handler ?? null
      setChatSending(false)
      setChatHistory(prev => [...prev, { id: generateId(), role: 'assistant', text: response }])
    },
  }), [removeToast, withBtns, openModal, closeModalIfGroupAtMost, prepareForGroup, setSlot, setChatVisibleSynced])


  const slot = document.getElementById('tbs-messages-slot')

  return (
    <>
      {createPortal(
        <>
          {/* Toasts — centrés horizontalement */}
          {toasts.length > 0 && (
            <div style={{
              position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
              zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, width: 380,
              pointerEvents: 'none',
            }}>
              {toasts.map(t => (
                <div key={t.id} style={{ pointerEvents: 'auto' }}>
                  <Toast {...t} onClose={() => removeToast(t.id)} />
                </div>
              ))}
            </div>
          )}

          {/* Groupe 1 */}
          {modal1?._modal === 'messageBox'       && <MessageBox       {...modal1} onClose={closeModal1} zIndex={modal1.__z} />}
          {modal1?._modal === 'confirmationBox'  && <ConfirmationBox  {...modal1} onClose={closeModal1} zIndex={modal1.__z} />}
          {modal1?._modal === 'waitingBox'       && <WaitingBox       {...modal1} zIndex={modal1.__z} />}
          {/* Groupe 2 */}
          {modal2?._modal === 'promptSimple'     && <PromptSimple     {...modal2} onClose={closeModal2} zIndex={modal2.__z} />}
          {modal2?._modal === 'promptCombo'      && <PromptCombo      {...modal2} onClose={closeModal2} zIndex={modal2.__z} />}
          {modal2?._modal === 'promptHtmlEditor' && <PromptHtmlEditor {...modal2} onClose={closeModal2} zIndex={modal2.__z} />}
          {modal2?._modal === 'promptACEEditor'  && <PromptACEEditor  {...modal2} onClose={closeModal2} zIndex={modal2.__z} />}
          {/* Groupe 3 */}
          {modal3?._modal === 'popupInplace'     && <PopupInplace     {...modal3} onClose={closeModal3} zIndex={modal3.__z} />}
          {modal3?._modal === 'modalDialogBox'   && <ModalDialogBox   key={modal3.__z} {...modal3} onClose={closeModal3} zIndex={modal3.__z} />}

          {chatVisible && (
            <ChatWidget
              open={chatOpen}
              onToggle={() => setChatOpen(o => !o)}
              onHide={handleHideChatButton}
              history={chatHistory}
              sending={chatSending}
              onSend={handleSendChatMessage}
            />
          )}
        </>,
        document.body
      )}

      {slot && createPortal(
        <MessagesFrame messages={messages} visible={frameVisible} onHide={() => setFrameVisible(false)} />,
        slot
      )}
    </>
  )
})

export default UserMessages
